import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  Home,
  Database,
  Bot,
  Users,
  Settings,
  BarChart3,
  Menu,
  X,
  Package,
  Mail,
  DollarSign,
  Cog,
  Shield,
  Workflow,
  Calculator,
  Lock,
  ScrollText,
  Building2,
  UserPlus,
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useOrgContext } from '@/contexts/OrgContext'

interface NavItem {
  to: string
  icon: any
  label: string
  future?: boolean
}

export function Sidebar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { isPlatformAdmin } = useAuth()
  const { hasOrgContext, selectedOrg } = useOrgContext()

  // Platform-level items (always shown unless in org context for non-platform-admins)
  const platformItems: NavItem[] = [
    { to: '/', icon: Home, label: 'Dashboard' },
    { to: '/knowledge-bases', icon: Database, label: 'Knowledge Bases' },
    { to: '/agents', icon: Bot, label: 'Agents' },
    { to: '/workflows', icon: Workflow, label: 'Workflows' },
    { to: '/artifacts', icon: Package, label: 'Artifacts' },
    { to: '/users', icon: Users, label: 'Users' },
    { to: '/credit-tracking', icon: DollarSign, label: 'Credit Tracking' },
    { to: '/pricing', icon: Calculator, label: 'Pricing' },
    { to: '/service-definitions', icon: Cog, label: 'Service Definitions' },
    { to: '/consent-policies', icon: Shield, label: 'Consent Policies' },
    { to: '/policies', icon: Lock, label: 'Permission Policies' },
    { to: '/audit-logs', icon: ScrollText, label: 'Audit Logs' },
    { to: '/mailing-list', icon: Mail, label: 'Mailing List' },
    { to: '/platform-settings', icon: Settings, label: 'Platform Settings' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics', future: true },
  ]

  // Org-scoped items
  const orgItems: NavItem[] = [
    { to: '/org-settings', icon: Settings, label: 'Org Settings' },
    { to: '/members', icon: UserPlus, label: 'Members' },
  ]

  // Build final nav list
  const navItems: NavItem[] = []

  // Organizations link (platform admin only)
  if (isPlatformAdmin) {
    navItems.push({ to: '/organizations', icon: Building2, label: 'Organizations' })
  }

  // Org-scoped items (when an org is selected)
  if (hasOrgContext) {
    navItems.push(...orgItems)
  }

  // Platform items: hidden when a non-platform-admin has EXPLICITLY selected an
  // org, so the view narrows to that org. Keyed on selectedOrg rather than
  // hasOrgContext on purpose: a dedicated tenant now always has (implicit) org
  // context, and gating on that would strip a tenant admin's entire nav down to
  // the two org items, removing pages they can reach today.
  if (selectedOrg === null || isPlatformAdmin) {
    navItems.push(...platformItems)
  }

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md bg-card shadow-md border border-border"
        >
          {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-background/80 backdrop-blur-sm z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card shadow-lg transform transition-transform lg:transform-none flex-shrink-0 h-screen",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 bg-primary">
            <h1 className="text-xl font-bold text-primary-foreground">
              Ponder Admin
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              // Add separator between org items and platform items
              const isFirstPlatformItem = hasOrgContext && isPlatformAdmin && item.to === '/'
              return (
                <div key={item.to}>
                  {isFirstPlatformItem && (
                    <div className="my-3 border-t border-border" />
                  )}
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        item.future && "opacity-50 cursor-not-allowed"
                      )
                    }
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.label}
                    {item.future && (
                      <span className="ml-auto text-xs bg-muted px-2 py-1 rounded">
                        Future
                      </span>
                    )}
                  </NavLink>
                </div>
              )
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Admin Panel v1.0
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}
